import type { DepositModalBaseProps } from '@/shared/types';
import { ApiErrorText, ModalComponentWrapper } from '@/shared/components';
import { WithdrawModalFooter } from '@/entities/operation';
import { NUMERIC_CONFIG } from '@/shared/config';
import { useTranslation } from 'react-i18next';
import {
  useWithdrawSccrsForm,
  useWithdrawSccrsModals,
  WithdrawSccrsForm,
  type WithdrawSccrsModalState,
} from '@/features/withdraw-sccrs';
import { useModalStore } from '@/shared/lib';
import { useEffect } from 'react';
import type { SccrsWithdrawFormValues } from '@/entities/sccrs';

interface WithdrawSccrsModalProps extends DepositModalBaseProps {
  initialData?: SccrsWithdrawFormValues;
}

export const WithdrawSccrsModal = ({
  onBack,
  title,
  initialData,
}: WithdrawSccrsModalProps) => {
  const { t } = useTranslation();
  const { setMultipleState, setCloseConfirmationRequired } = useModalStore();
  const { openConfirmationModal } = useWithdrawSccrsModals();

  const {
    sccrsForm: form,
    selectedCurrency,
    accountNumber,
    commissionData,
    totalAmount,
    watchedValues,
    platformOptions,
    isLoading,
    isFromDataLoading,
    error,
    hasWithdrawFee,
    handleFormSubmit,
    currencyOptions,
    formDataError,
    handleMaxClick,
    isDirty,
  } = useWithdrawSccrsForm({
    initialData: {
      currencyCode: initialData?.currencyCode || 'BYN',
      fls: initialData?.fls || '',
      platform: initialData?.platform || '',
      amount: initialData?.amount || '',
      includeFee: false,
    },
    onSuccess: data => {
      setMultipleState<WithdrawSccrsModalState>({
        withdrawSccrsFormData: watchedValues,
        platform: platformOptions.find(
          option => option.gamingHallId === data.platform
        ),
        gas: data.gas,
        selectedCurrency,
        accountNumber,
        commissionData,
        fee: commissionData?.feeAmount || 0,
        totalAmount,
        gamingHallId: data.platform,
      });

      openConfirmationModal();
    },
  });

  useEffect(() => {
    if (isDirty || initialData) {
      setCloseConfirmationRequired(true);
    }
  }, [isDirty]);

  const submitForm = () => {
    form.handleSubmit(handleFormSubmit)();
  };

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onBack}
      footerClassName="sm:px-0 px-0 before:invisible"
      footer={
        <WithdrawModalFooter
          precision={NUMERIC_CONFIG.precision.default}
          amount={totalAmount}
          assetCode={watchedValues.currencyCode}
          withdrawFee={commissionData?.feeAmount || 0}
          includeFee={hasWithdrawFee && Boolean(watchedValues.includeFee)}
          disabled={isFromDataLoading || isLoading}
          isLoading={isLoading}
          onSubmit={submitForm}
          submitLabel={t('withdrawMethods.crypto.submitButton')}
        />
      }>
      <WithdrawSccrsForm
        error={formDataError}
        hasWithdrawFee={hasWithdrawFee}
        form={form}
        handleFormSubmit={handleFormSubmit}
        currencyOptions={currencyOptions}
        isError={Boolean(formDataError) && !isLoading}
        platformOptions={platformOptions}
        selectedAsset={selectedCurrency}
        isLoading={isFromDataLoading}
        onMaxClick={handleMaxClick}
      />
      {!isLoading && error && <ApiErrorText error={error} />}
    </ModalComponentWrapper>
  );
};
